import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  Truck,
  Activity,
  Wrench,
  FileWarning,
  Navigation,
  Gauge,
  Fuel,
  ShieldAlert,
  RefreshCw,
  Eye,
  Clock,
  AlertTriangle,
} from 'lucide-react';

interface VehiclePin {
  id: string;
  name: string;
  vehicle_number: string;
  health_status: 'green' | 'yellow' | 'red';
  assigned_driver: string;
  speed: number;
  last_seen: string;
}

interface MaintenanceItem {
  id: string;
  vehicle_name: string;
  service_type: string;
  urgency: 'overdue' | 'this_week' | 'upcoming';
  due_date: string;
}

interface FuelAnomaly {
  id: string;
  vehicle_name: string;
  description: string;
  detected_at: string;
}

interface SafetyEvent {
  id: string;
  vehicle_name: string;
  event_type: string;
  description: string;
  occurred_at: string;
}

interface CommandCenterData {
  summary: {
    total_vehicles: number;
    active: number;
    in_maintenance: number;
    overdue_maintenance: number;
    due_this_week: number;
    expiring_inspections: number;
    fuel_anomalies: number;
    active_dispatches: number;
    unacknowledged_alerts: number;
  };
  vehicle_pins: VehiclePin[];
  maintenance_queue: MaintenanceItem[];
  inspection_countdown: unknown[];
  fuel_anomalies: FuelAnomaly[];
  safety_events: SafetyEvent[];
  active_dispatches: unknown[];
  recent_alerts: unknown[];
}

const EMPTY: CommandCenterData = {
  summary: {
    total_vehicles: 0,
    active: 0,
    in_maintenance: 0,
    overdue_maintenance: 0,
    due_this_week: 0,
    expiring_inspections: 0,
    fuel_anomalies: 0,
    active_dispatches: 0,
    unacknowledged_alerts: 0,
  },
  vehicle_pins: [],
  maintenance_queue: [],
  inspection_countdown: [],
  fuel_anomalies: [],
  safety_events: [],
  active_dispatches: [],
  recent_alerts: [],
};

const HEALTH_DOTS: Record<string, string> = {
  green: '#22C55E',
  yellow: '#EAB308',
  red: '#EF4444',
};

const URGENCY_COLORS: Record<string, { bg: string; text: string }> = {
  overdue: { bg: 'rgba(239,68,68,0.15)', text: '#EF4444' },
  this_week: { bg: 'rgba(234,179,8,0.15)', text: '#EAB308' },
  upcoming: { bg: 'rgba(59,130,246,0.15)', text: '#3B82F6' },
};

const EVENT_ICONS: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  speeding: Gauge,
  harsh_braking: AlertTriangle,
  harsh_acceleration: Activity,
  idle: Clock,
};

export default function CommandCenterPage() {
  const { data, isLoading, error } = useApi<CommandCenterData>('/api/command-center');
  const d = data ?? EMPTY;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Command Center"
        description="Vehicle health and operations overview"
        actions={
          <div className="flex items-center gap-3">
            {isLoading && <Spinner size={16} />}
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
              style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}
            >
              <RefreshCw className="w-3 h-3" />
              Auto-refresh 30s
            </span>
          </div>
        }
      />

      {error && (
        <div className="rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" style={{ color: '#EF4444' }} />
            <span className="text-sm" style={{ color: '#EF4444' }}>Unable to load command center data. Please try again.</span>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard icon={Truck} label="Total Vehicles" value={d.summary.total_vehicles} />
        <StatCard icon={Activity} label="Active" value={d.summary.active} accent="success" />
        <StatCard icon={Wrench} label="Overdue Maint" value={d.summary.overdue_maintenance} accent="destructive" />
        <StatCard icon={FileWarning} label="Expiring Inspections" value={d.summary.expiring_inspections} accent="warning" />
        <StatCard icon={Navigation} label="Active Dispatches" value={d.summary.active_dispatches} />
      </div>

      {/* Vehicle Pins Grid */}
      <div>
        <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--foreground)' }}>
          Vehicle Status
        </h2>
        {d.vehicle_pins.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Eye className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--muted-foreground)' }} />
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No vehicle data available</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {d.vehicle_pins.map((v) => (
              <div
                key={v.id}
                className="rounded-xl p-4"
                style={{
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                  backdropFilter: 'blur(var(--glass-blur))',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{v.name}</span>
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ background: HEALTH_DOTS[v.health_status] ?? HEALTH_DOTS.green }}
                  />
                </div>
                <div className="text-xs space-y-1" style={{ color: 'var(--muted-foreground)' }}>
                  <div className="flex justify-between">
                    <span>#{v.vehicle_number}</span>
                    <span>{v.speed} mph</span>
                  </div>
                  <div>{v.assigned_driver}</div>
                  <div className="text-[10px]">Last seen: {new Date(v.last_seen).toLocaleTimeString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Maintenance Urgency Queue */}
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
              <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
                <Wrench className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                Maintenance Queue
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                    {['Vehicle', 'Service', 'Urgency', 'Due'].map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.maintenance_queue.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                        No maintenance items
                      </td>
                    </tr>
                  ) : (
                    d.maintenance_queue.map((m) => {
                      const ucEntry = URGENCY_COLORS[m.urgency] ?? { bg: 'rgba(59,130,246,0.15)', text: '#3B82F6' };
                      return (
                        <tr key={m.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                          <td className="px-4 py-3" style={{ color: 'var(--foreground)' }}>{m.vehicle_name}</td>
                          <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{m.service_type}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: ucEntry.bg, color: ucEntry.text }}>
                              {m.urgency.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                            {new Date(m.due_date).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Right column: Fuel Anomalies + Safety Events */}
        <div className="space-y-6">
          {/* Fuel Anomalies */}
          <Card>
            <CardContent className="p-0">
              <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
                <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
                  <Fuel className="w-4 h-4" style={{ color: '#EAB308' }} />
                  Fuel Anomalies
                </h2>
              </div>
              <div className="divide-y" style={{ borderColor: 'var(--glass-border)' }}>
                {d.fuel_anomalies.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    No anomalies detected
                  </div>
                ) : (
                  d.fuel_anomalies.map((f) => (
                    <div key={f.id} className="px-4 py-3 flex items-start gap-3">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#EAB308' }} />
                      <div>
                        <div className="text-sm" style={{ color: 'var(--foreground)' }}>{f.vehicle_name}</div>
                        <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{f.description}</div>
                        <div className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                          {new Date(f.detected_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Safety Events */}
          <Card>
            <CardContent className="p-0">
              <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
                <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
                  <ShieldAlert className="w-4 h-4" style={{ color: '#EF4444' }} />
                  Safety Events
                </h2>
              </div>
              <div className="divide-y" style={{ borderColor: 'var(--glass-border)' }}>
                {d.safety_events.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    No recent safety events
                  </div>
                ) : (
                  d.safety_events.map((e) => {
                    const EIcon = EVENT_ICONS[e.event_type] ?? ShieldAlert;
                    return (
                      <div key={e.id} className="px-4 py-3 flex items-start gap-3">
                        <EIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
                        <div>
                          <div className="text-sm" style={{ color: 'var(--foreground)' }}>{e.vehicle_name}</div>
                          <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{e.description}</div>
                          <div className="text-[10px] mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                            {new Date(e.occurred_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
